/**
 * @fileoverview Empty / error state block.
 * @description A soft tinted icon tile, a plain title, one sentence of help and, where it helps,
 * the next step as a button. Used two ways: `compact`, sitting inside a table or card where the
 * rows would be (nothing matches, nothing yet), and full-size, filling a page body when the whole
 * page failed to load. Every blank or failed screen in the sales area uses this one look, so a
 * person always sees the same "what happened, what to do" shape.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import Icon from '@/shared/components/atoms/Icon'

import type { IconName } from '@/shared/components/atoms/Icon'
import type { ReactNode } from 'react'

export type StateTone = 'info' | 'good' | 'warn' | 'bad' | 'quiet'

interface StateMessageProps {
  icon: IconName
  tone?: StateTone
  title: string
  description?: string | undefined
  actions?: ReactNode
  detail?: string | undefined
  compact?: boolean
  testId?: string | undefined
}

const TONE_CLASS_NAMES: Record<StateTone, string> = {
  info: 'bg-info-bg text-primary',
  good: 'bg-success-bg text-teal',
  warn: 'bg-status-warning-bg text-status-warning',
  bad: 'bg-danger-bg text-danger',
  quiet: 'bg-neutral-bg text-slate',
}

/**
 * @component
 * @description Renders a centered icon, title, optional description, optional actions, and an
 * optional monospace detail line (e.g. an error code). Only a warn or bad state is announced (as an
 * alert); a calm empty state is not a live region, and the title is a paragraph, not a heading, so
 * it never breaks a page's heading outline.
 * @param {StateMessageProps} props - The block's content and size.
 * @param {IconName} props.icon - The icon shown in the tinted tile.
 * @param {StateTone} [props.tone] - The tile's tint: info, good, warn, bad, or quiet (default).
 * @param {string} props.title - The short heading.
 * @param {string} [props.description] - One sentence of help under the heading.
 * @param {ReactNode} [props.actions] - Buttons rendered in a centered row under the text.
 * @param {string} [props.detail] - Small monospace line, such as "Error SERVER_ERROR".
 * @param {boolean} [props.compact] - Smaller variant for use inside a table or card.
 * @param {string} [props.testId] - `data-testid` for the block's root.
 */
function StateMessage({
  icon,
  tone = 'quiet',
  title,
  description,
  actions,
  detail,
  compact = false,
  testId,
}: StateMessageProps) {
  const isAlert = tone === 'bad' || tone === 'warn'

  return (
    <div
      role={isAlert ? 'alert' : undefined}
      data-testid={testId}
      className={`flex flex-col items-center text-center ${compact ? 'gap-2 px-5 py-9' : 'gap-2.5 px-6 py-14'}`}
    >
      <span
        className={`mb-1 inline-flex items-center justify-center rounded-full ${TONE_CLASS_NAMES[tone]} ${compact ? 'size-11' : 'size-16'}`}
      >
        <Icon name={icon} size={compact ? 22 : 30} />
      </span>
      <p className={`font-bold text-ink ${compact ? 'text-md' : 'text-lg'}`}>{title}</p>
      {description && (
        <p
          className={`leading-relaxed text-slate ${compact ? 'max-w-state-compact text-compact' : 'max-w-state text-sm'}`}
        >
          {description}
        </p>
      )}
      {actions && <div className="mt-2 flex flex-wrap justify-center gap-2.5">{actions}</div>}
      {detail && <p className="mt-2 font-mono text-eyebrow text-footer">{detail}</p>}
    </div>
  )
}

export default StateMessage
